// Subscription composable — email subscription flow + feed URLs
import { ref, computed } from 'vue'
import { useI18n } from './useI18n'
import { useEvents } from './useEvents'
import { useToasts } from './useToasts'
import { scopeLabels } from '@/i18n'

// Shared reactive state (module-level, persists across component instances within a session)
const preferencesToken = ref<string | null>(null)
const preferencesData = ref<{ services: string[]; severities: string[]; language: string; maskedEmail: string } | null>(null)
const isConfirmed = ref(false)
const isPending = ref(false)
const pendingEmail = ref('')
const partialEmail = ref('')
const subscribedServices = ref<string[]>([])
const subscribedSeverities = ref<string[]>([])

export function useSubscription() {
  const { lang, t } = useI18n()
  const { servicesList } = useEvents()
  const { showToast } = useToasts()

  const storedEmail = computed(() => {
    if (isConfirmed.value) return partialEmail.value
    return pendingEmail.value
  })

  function getStoredServices(): string[] {
    if (subscribedServices.value.length > 0) return [...subscribedServices.value]
    return servicesList.value.map((s) => s.name)
  }

  function getStoredSeverities(): string[] {
    if (subscribedSeverities.value.length > 0) return [...subscribedSeverities.value]
    return ['info', 'minor', 'major', 'critical']
  }

  function validateEmail(email: string): { valid: boolean; error?: string } {
    if (!email) return { valid: false, error: t('subscribe-email-required') }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return { valid: false, error: t('subscribe-email-invalid') }
    return { valid: true }
  }

  async function subscribe(
    email: string,
    selectedScopes: string[],
    selectedSeverities: string[]
  ): Promise<boolean> {
    // Expand scopes to services
    let selectedServices: string[] = []
    selectedScopes.forEach((scope) => {
      const scopeServices = servicesList.value.filter((s) => s.scope === scope)
      selectedServices.push(...scopeServices.map((s) => s.name))
    })
    selectedServices = Array.from(new Set(selectedServices))

    if (selectedServices.length === 0) {
      showToast(t('subscribe-services-required'))
      return false
    }
    if (selectedSeverities.length === 0) {
      showToast(t('subscribe-error'))
      return false
    }

    const payload = {
      email,
      language: lang.value,
      services: selectedServices,
      severities: selectedSeverities,
    }

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        isPending.value = true
        pendingEmail.value = email

        const [local, domain] = email.split('@')
        partialEmail.value = `${local.substring(0, 2)}***@${domain}`

        showToast(t('subscribe-success'), 'success')
        return true
      } else {
        throw new Error('Subscription failed')
      }
    } catch {
      showToast(t('subscribe-error'))
      return false
    }
  }

  async function checkConfirmationToken(): Promise<boolean> {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('token')
    if (!token) return false

    try {
      const response = await fetch('/api/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await response.json()

      if (response.ok) {
        isPending.value = false
        pendingEmail.value = ''
        isConfirmed.value = true
        showToast(t('subscribe-confirmed'), 'success')
      } else {
        showToast(data.message || t('subscribe-error'))
      }
    } catch {
      showToast(t('subscribe-error'))
    }

    url.searchParams.delete('token')
    window.history.replaceState({}, '', url.toString())
    return true
  }

  async function checkUnsubscribeToken(): Promise<boolean> {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('unsubscribe')
    if (!token) return false

    try {
      const response = await fetch('/api/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await response.json()

      if (response.ok) {
        isConfirmed.value = false
        isPending.value = false
        pendingEmail.value = ''
        partialEmail.value = ''
        subscribedServices.value = []
        subscribedSeverities.value = []
        preferencesToken.value = null
        preferencesData.value = null
        showToast(t('unsubscribe-success'), 'success')
      } else {
        showToast(data.message || t('unsubscribe-error'))
      }
    } catch {
      showToast(t('unsubscribe-error'))
    }

    url.searchParams.delete('unsubscribe')
    window.history.replaceState({}, '', url.toString())
    return true
  }

  async function checkPreferencesToken(): Promise<boolean> {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('preferences')
    if (!token) return false

    try {
      const response = await fetch('/api/subscription/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (response.ok) {
        const data = await response.json()
        preferencesToken.value = token
        preferencesData.value = {
          services: data.services || [],
          severities: data.severities || [],
          language: data.language || lang.value,
          maskedEmail: data.masked_email || '',
        }
        // Also mark as confirmed and populate state
        isConfirmed.value = true
        subscribedServices.value = data.services || []
        subscribedSeverities.value = data.severities || []
        if (data.masked_email) {
          partialEmail.value = data.masked_email
        }
        // Auto-open the subscription modal
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('open-subscription-modal', { detail: { preferences: true } }))
        }, 300)
      } else {
        const data = await response.json()
        showToast(data.error || t('preferences-error'))
      }
    } catch {
      showToast(t('preferences-error'))
    }

    url.searchParams.delete('preferences')
    window.history.replaceState({}, '', url.toString())
    return true
  }

  async function updatePreferences(
    token: string,
    selectedScopes: string[],
    selectedSeverities: string[]
  ): Promise<boolean> {
    // Expand scopes to services
    let selectedServices: string[] = []
    selectedScopes.forEach((scope) => {
      const scopeServices = servicesList.value.filter((s) => s.scope === scope)
      selectedServices.push(...scopeServices.map((s) => s.name))
    })
    selectedServices = Array.from(new Set(selectedServices))

    if (selectedServices.length === 0) {
      showToast(t('subscribe-services-required'))
      return false
    }
    if (selectedSeverities.length === 0) {
      showToast(t('subscribe-error'))
      return false
    }

    const payload = {
      token,
      services: selectedServices,
      severities: selectedSeverities,
      language: lang.value,
    }

    try {
      const response = await fetch('/api/subscription/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        // Update reactive state with new preferences
        subscribedServices.value = selectedServices
        subscribedSeverities.value = selectedSeverities
        showToast(t('preferences-updated'), 'success')
        preferencesToken.value = null
        preferencesData.value = null
        return true
      } else {
        const data = await response.json()
        showToast(data.error || t('preferences-error'))
        return false
      }
    } catch {
      showToast(t('preferences-error'))
      return false
    }
  }

  function getRssUrl(): string {
    return new URL(`/api/feeds/rss/${lang.value}`, window.location.href).href
  }

  function getAtomUrl(): string {
    return new URL(`/api/feeds/atom/${lang.value}`, window.location.href).href
  }

  function getSlackCommand(): string {
    const slackUrl = new URL(`/api/feeds/slack/${lang.value}`, window.location.href).href
    return `/feed subscribe ${slackUrl}`
  }

  async function copyToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch {
        // Fall through to fallback
      }
    }
    const temp = document.createElement('textarea')
    temp.value = text
    temp.setAttribute('readonly', '')
    temp.style.position = 'fixed'
    temp.style.top = '-9999px'
    document.body.appendChild(temp)
    temp.select()
    let didCopy = false
    try {
      didCopy = document.execCommand('copy')
    } catch {
      // Ignore
    }
    temp.remove()
    return didCopy
  }

  async function loadSubscriptionState(): Promise<void> {
    try {
      const response = await fetch('/api/subscription/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (response.ok) {
        const data = await response.json()
        isConfirmed.value = true
        subscribedServices.value = data.services || []
        subscribedSeverities.value = data.severities || []
        if (data.masked_email) {
          partialEmail.value = data.masked_email
        }
      }
    } catch {
      // Not subscribed or no cookie — that's fine
    }
  }

  return {
    isConfirmed,
    isPending,
    storedEmail,
    getStoredServices,
    getStoredSeverities,
    validateEmail,
    subscribe,
    checkConfirmationToken,
    checkUnsubscribeToken,
    checkPreferencesToken,
    loadSubscriptionState,
    updatePreferences,
    preferencesToken,
    preferencesData,
    getRssUrl,
    getAtomUrl,
    getSlackCommand,
    copyToClipboard,
  }
}
